<template>
  <FieldViewLocationNotFound
    v-if="!location && !isLoadingLocations && !isFetched"
    :is-loading="isLoadingLocations"
    @dismiss="handleDismiss"
  />
  <FormLayout
    v-else
    dense
    no-scroll
    :is-loading="isLoadingLocations"
    :title="location?.location"
    :overline="location?.parentName"
    :overline-link-to="{
      name: 'fvnext-info-panel',
      params: { id: location?.parentId, source: 'locations' },
    }"
    @on-dismiss="handleDismiss"
  >
    <template #extras>
      <div v-if="location" class="location-details-row">
        <div class="location-details-flex">
          <div class="location-details">
            <vs-text type="caption">
              <span>{{ location?.ownerShortName ?? location.ownerName }}</span>
              <span>{{ ' · ' + location?.locationTypeName }}</span>
              <span v-if="location?.locationStatusDescription"
                >{{ ' · ' }}
                <v-icon
                  class="location-status-icon"
                  x-small
                  :color="location.locationStatusColorHexCode"
                >
                  mdi-circle </v-icon
                >{{ location?.locationStatusDescription }}</span
              >
            </vs-text>
          </div>
        </div>
      </div>
      <div v-if="location?.latitude && location?.longitude">
        <vs-text type="caption">{{ location?.latitude }}, {{ location?.longitude }}</vs-text>
      </div>
      <div
        v-if="location?.customData && locationTypeCustomFieldsSchema"
        class="location-details-row"
      >
        <field-view-readonly-custom-data
          :custom-data="location?.customData"
          :root-schema="locationTypeCustomFieldsSchema"
        />
      </div>
      <div v-if="location?.description" class="location-details-row">
        <vs-text type="caption">
          <span
            :class="{
              'truncatable-description': true,
              'text-truncate': hasDescriptionToggle && !isShowingMore,
            }"
            >{{
              isShowingMore ? location.description : location.description.replaceAll('\n', ' ')
            }}</span
          ><span v-if="hasDescriptionToggle" class="more-description-toggle" @click="toggleMore">{{
            isShowingMore ? 'Less' : 'More'
          }}</span>
        </vs-text>
      </div>
      <div v-if="location">
        <span>
          <TagsDisplay :tags="location.tags" class="mr-1" />
          <vs-button
            v-if="location"
            :label="location.tags && location.tags.length > 0 ? 'Edit tags' : 'Add tags'"
            type="secondary"
            :icon="location.tags && location.tags.length > 0 ? 'mdi-pencil' : 'mdi-plus'"
            small
            @click="showTagsEditor = true"
          />
        </span>
      </div>
    </template>
    <div v-if="isLocationGrantedAccessToCurrentUserAccessibleHubs" class="content-inner">
      <div class="location-tabs">
        <v-tabs v-model="tabsModel" class="tabs" fixed-tabs>
          <v-tab key="Jobs">Jobs</v-tab>
          <v-tab key="Inventory">Inventory</v-tab>
          <v-tab v-if="sensorsList?.length" key="Sensors">Sensors</v-tab>
        </v-tabs>
      </div>
      <v-tabs-items v-model="tabsModel" class="tabbed-content">
        <v-tab-item key="Jobs">
          <location-details-job-tab :location-id="locationId" />
        </v-tab-item>
        <v-tab-item key="Inventory">
          <FieldViewLocationInventory
            :key="`location-${locationId}`"
            :location="location"
            :can-edit-inventory="canEditLocation"
            @edit-inventory="editInventory"
          />
        </v-tab-item>
        <v-tab-item v-if="sensorsList?.length" key="Sensors">
          <location-details-sensors-tab :location-id="locationId" :sensors-list="sensorsList" />
        </v-tab-item>
      </v-tabs-items>
    </div>
    <div v-else class="d-flex flex-column justify-center fill-height">
      <vs-text type="paragraph" style="font-size: large; font-weight: bold">{{
        grantedAccessMessage
      }}</vs-text>
    </div>
    <template #action-bar>
      <vs-wrapper direction="horizontal">
        <vs-button
          v-if="canEditLocation"
          label="Edit location"
          type="secondary"
          full-width
          @click="handleEditLocationClicked"
        />
        <new-job-button
          v-if="isLocationGrantedAccessToCurrentUserAccessibleHubs"
          full-width
          label="New job here"
          parent-route-short-name="fvnext"
          :extra-nav-params="{
            source: 'locations',
            sourceId: location?.id?.toString(),
          }"
          :defaults="{
            locationId: location?.id?.toString(),
          }"
          :limit-hubs-to="location?.accessBusinessUnitIds ?? undefined"
          :show-manage-clients="false"
        />
        <vs-button
          v-if="
            !isLocationGrantedAccessToCurrentUserAccessibleHubs &&
            canFullyEditLocation(location?.ownerId ?? '')
          "
          label="Add to hubs"
          type="secondary"
          full-width
          @click="
            () => {
              handleEditLocationClicked()
              shouldFocusHubsSelector = true
            }
          "
        />
      </vs-wrapper>
    </template>
    <template #bottom-sheet>
      <vs-bottom-sheet v-if="location?.id" :is-visible="isBottomSheetVisible">
        <FieldViewAdjustmentForm
          v-if="showingAdjustmentForm"
          :location-id="location?.id"
          :asset-id="adjustmentFormAssetId ?? 0"
          @on-dismiss="dismissAdjustmentForm"
        />
        <LocationTagsEditor
          :location-id="locationIdRef"
          v-else-if="showTagsEditor"
          @dismiss="showTagsEditor = false"
        />
        <field-view-location-details-edit
          v-else-if="canOnlyEditLocationDetails"
          :custom-data="location?.customData"
          :description="location?.description"
          :location-id="location?.id"
          :name="locationTitle"
          :root-schema="locationTypeCustomFieldsSchema"
          :status-id="location?.colorId"
          @dismiss="handleEditingSheetDismiss"
          @save="handleEditingSheetSave"
        />
        <FieldViewLocationForm
          v-else
          :id="location?.id"
          :focus-hubs-selector="shouldFocusHubsSelector"
          @dismiss="handleEditingSheetDismiss"
          @save="handleEditingSheetSave"
        />
      </vs-bottom-sheet>
    </template>
  </FormLayout>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import FormLayout from '../common/FormLayout.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'
import NewJobButton from '@/components/common/NewJobButton.vue'
import FieldViewReadonlyCustomData from './FieldViewReadonlyCustomData.vue'
import FieldViewLocationDetailsEdit from './FieldViewLocationDetailsEdit.vue'
import FieldViewLocationForm from './FieldViewLocationForm.vue'
import FieldViewAdjustmentForm from './FieldViewAdjustmentForm.vue'
import FieldViewLocationInventory from './FieldViewLocationInventory.vue'
import LocationDTO from '@/models/location'
import FieldViewLocationNotFound from './FieldViewLocationNotFound.vue'
import LocationDetailsJobTab from './LocationDetailsJobTab.vue'
import LocationDetailsSensorsTab from './LocationDetailsSensorsTab.vue'
import { useLocationQuery } from '@/api/locations'
import { useGetLocationTypesQuery } from '@/api/location-types'
import { useStore } from 'vuex-composition-helpers'
import { useMap } from './mapbox/context'
import { useFieldView } from './FieldViewState'
import { intersection } from 'lodash'
import { useSensorsForLocationQuery } from '@/api/sensors'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import VsText from '@/components/vision/VsText.vue'
import LocationTagsEditor from '@/components/common/tags/LocationTagsEditor.vue'
import TagsDisplay from '@/components/common/tags/TagsDisplay.vue'

const props = defineProps<{
  locationId: string | number | null
}>()

const emits = defineEmits<{
  (e: 'dismiss')
}>()

const locationIdRef = toRef(props, 'locationId')
const {
  data: location,
  refetch,
  isFetched,
  isLoading: isLoadingLocations,
} = useLocationQuery(locationIdRef)

const { data: sensorsList } = useSensorsForLocationQuery(locationIdRef)

const isBottomSheetVisible = computed(
  () => showingEditingBottomSheet.value || showingAdjustmentForm.value || showTagsEditor.value,
)

const { map } = useMap()
const { setEditMode, setSelectedLocation, upsertModifiedLocation } = useFieldView()
const showingEditingBottomSheet = ref<boolean>(false)
const showTagsEditor = ref(false)

watch(
  location,
  async (nextLocation) => {
    if (nextLocation) {
      if (showingEditingBottomSheet.value) {
        showingEditingBottomSheet.value = false
      }
      setSelectedLocation(nextLocation)
      flyToLocation(nextLocation)
    }
  },
  {
    immediate: true,
  },
)

const tabsModel = ref<any>()

const maxDescriptionLength = 60
const isShowingMore = ref<boolean>(false)

const hasDescriptionToggle = computed(() => {
  const desc = location?.value?.description || ''
  return desc.length > maxDescriptionLength
})

const toggleMore = () => {
  isShowingMore.value = !isShowingMore.value
}

const { data: locationTypesResults } = useGetLocationTypesQuery()
const locationType = computed(() =>
  locationTypesResults.value?.find((x) => x.id === location.value?.locationTypeId),
)

const locationTypeCustomFieldsSchema = computed<object | null>(() => {
  if (locationType.value?.customFieldsSchema == null) {
    return null
  }
  return locationType.value?.customFieldsSchema as object
})

const locationTitle = computed<string>(() => {
  return location.value?.location ?? 'Location'
})

const store = useStore()

const canEditLocationDetails = computed<(locationOrgId?: string | null) => boolean>(
  () => store.getters['permissions/canEditLocationDetails'],
)

const canFullyEditLocation = computed<(locationOrgId?: string | null) => boolean>(
  () => store.getters['permissions/canFullyEditLocation'],
)

const accessibleHubIds = computed<string[]>(() => store.getters['permissions/getAccessibleHubIds'])

const isLocationGrantedAccessToCurrentUserAccessibleHubs = computed(
  () =>
    intersection(accessibleHubIds.value, location.value?.accessBusinessUnitIds ?? []).length > 0,
)
const shouldFocusHubsSelector = ref(false)

const grantedAccessMessage = computed(() => {
  if (isLocationGrantedAccessToCurrentUserAccessibleHubs.value) {
    return
  }

  if (canFullyEditLocation.value(location.value?.ownerId)) {
    return 'Add this location to a hub in order to request jobs here.'
  } else {
    return (
      'This location must be granted access in order to request jobs here. ' +
      'Contact your IronSight administrator to gain access.'
    )
  }
})

const adjustmentFormAssetId = ref<number | null>(null)

const showingAdjustmentForm = computed<boolean>(() => {
  return adjustmentFormAssetId.value !== null
})

const handleEditingSheetDismiss = () => {
  showingEditingBottomSheet.value = false
  setEditMode(false)
  shouldFocusHubsSelector.value = false
}

const dismissAdjustmentForm = () => {
  adjustmentFormAssetId.value = null
}

const editInventory = (assetId: number) => {
  adjustmentFormAssetId.value = assetId
}

const handleEditingSheetSave = async () => {
  await refetch()
  setEditMode(false)
  upsertModifiedLocation(location.value!)
  showingEditingBottomSheet.value = false
  shouldFocusHubsSelector.value = false
}

const handleEditLocationClicked = () => {
  setEditMode(true)
  showingEditingBottomSheet.value = true
}

const canEditLocation = computed<boolean>(() => {
  return canEditLocationDetails.value(location.value?.ownerId)
})

const canOnlyEditLocationDetails = computed<boolean>(() => {
  return (
    canEditLocationDetails.value(location.value?.ownerId) &&
    !canFullyEditLocation.value(location.value?.ownerId)
  )
})

const handleDismiss = () => {
  if (!isBottomSheetVisible.value) {
    emits('dismiss')
  }
}

function flyToLocation(location: LocationDTO) {
  const coordinates = { lon: location.longitude!, lat: location.latitude! }
  map.value?.flyTo({
    center: coordinates,
    zoom: 16,
    animate: map.value?.getBounds().contains(coordinates),
  })
}
</script>

<style scoped>
.full-height-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.scroll-region {
  height: 100%;
  overflow: scroll;
  padding: 16px;
}

.bottom-region {
  flex-basis: 80px;
  box-shadow: 0px -1px 4px 0px #00000030;
  display: flex;
  align-items: center;
  justify-content: center;
}

.location-details-flex {
  display: flex;
  width: 100%;
  align-items: center;
}

.location-details {
  width: 100%;
}
.location-details-row {
  clear: both;
  margin-bottom: var(--space-smallest);
}
.location-details-row:after {
  content: '';
  clear: both;
  display: table;
}

.truncatable-description {
  display: block;
  max-width: 100%;
  line-height: 1.3;
  float: left;
}

.truncatable-description.text-truncate {
  max-width: calc(100% - 50px);
  line-height: 1.3;
}
.more-description-toggle {
  text-decoration: underline;
  color: var(--color-blue);
  cursor: pointer;
  float: left;
  line-height: 1.3;
}

.attribute {
  display: flex;
  align-self: center;
  justify-content: flex-start;
  column-gap: 0.4em;
  align-items: center;
}

.location-status-icon {
  vertical-align: baseline !important;
  padding-right: var(--space-smaller);
}

.content-inner {
  margin-left: calc(-1 * var(--space-large));
  margin-right: calc(-1 * var(--space-large));
  width: calc(100% + 2 * var(--space-large));
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tabs {
  padding-left: var(--space-large);
  padding-right: var(--space-large);
}

.tabbed-content {
  padding: var(--space-base) var(--space-large) var(--space-small);
  height: 100%;
  overflow: scroll;
}
</style>
